/**
 * INACTIVITY-REVIEW-004/005 — compact premium confirmation shown before
 * "Mark Day as Reviewed" when employee-level reviews are still pending.
 * Presentation only; the three actions are wired by the parent.
 */
import React from 'react';
import { AlertTriangle, Loader2, ListChecks, Users, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { formatDateOnly } from '../../utils/formatters';

const TONES = {
  slate: { card: 'bg-slate-50 border-slate-200', icon: 'bg-white text-slate-500', value: 'text-slate-900' },
  green: { card: 'bg-emerald-50/70 border-emerald-200', icon: 'bg-white text-emerald-600', value: 'text-emerald-700' },
  amber: { card: 'bg-amber-50/70 border-amber-200', icon: 'bg-white text-amber-600', value: 'text-amber-800' },
  blue: { card: 'bg-blue-50/70 border-blue-200', icon: 'bg-white text-blue-600', value: 'text-blue-700' },
};

const Stat = ({ icon: Icon, label, value, tone = 'slate', delay = 0, testId }) => {
  const t = TONES[tone];
  return (
    <div className={`min-w-0 flex items-center gap-3 rounded-xl border px-3 py-2.5 ${t.card} motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1 motion-safe:duration-200 motion-safe:fill-mode-both`} style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
      <span className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 shadow-sm ${t.icon}`}><Icon className="h-4 w-4" aria-hidden="true" /></span>
      <div className="min-w-0">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-slate-500 leading-tight">{label}</div>
        <div className={`text-[20px] font-bold leading-tight tabular-nums ${t.value}`}>{value}</div>
      </div>
    </div>
  );
};

const btn = 'inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-lg text-[13px] font-semibold whitespace-nowrap transition-[background-color,color,box-shadow,transform] duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed motion-safe:hover:-translate-y-px';

export const DayReviewConfirmDialog = ({ open, onOpenChange, date, counts, busy, onProceed, onReviewPending }) => {
  if (!counts) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-2rem)] sm:max-w-[540px] max-h-[calc(100vh-2rem)] overflow-y-auto overflow-x-hidden p-0 gap-0 rounded-2xl border-slate-200 shadow-2xl motion-reduce:animate-none"
        data-testid="day-review-confirm-dialog"
      >
        <DialogHeader className="min-w-0 px-5 pt-5 pb-4 pr-12 border-b border-slate-100 text-left">
          <div className="flex items-start gap-3 min-w-0">
            <span className="h-9 w-9 rounded-xl bg-amber-50 border border-amber-100 text-amber-600 grid place-items-center shrink-0"><AlertTriangle className="h-[18px] w-[18px]" aria-hidden="true" /></span>
            <div className="min-w-0">
              <DialogTitle className="text-[16px] font-bold text-slate-900 leading-tight">Pending Employee Reviews</DialogTitle>
              <DialogDescription className="text-[12.5px] text-slate-600 mt-1" data-testid="day-review-confirm-text">
                You are about to mark <span className="font-semibold text-slate-800">{formatDateOnly(date)}</span> as Reviewed.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="min-w-0 px-5 py-4 space-y-3.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <Stat icon={Users} label="Employees with Inactivity" value={counts.total} tone="slate" delay={0} testId="day-review-count-total" />
            <Stat icon={CheckCircle2} label="Reviewed" value={counts.reviewed} tone="green" delay={40} testId="day-review-count-reviewed" />
            <Stat icon={Clock} label="Needs Review" value={counts.needs_review} tone="amber" delay={80} testId="day-review-count-needs" />
            <Stat icon={RefreshCw} label="Updated Since Review" value={counts.updated_since_review} tone="blue" delay={120} testId="day-review-count-updated" />
          </div>
          <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-3 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-300 motion-safe:fill-mode-both" style={{ animationDelay: '160ms' }} data-testid="day-review-warning">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-[12.5px] leading-relaxed text-amber-900 min-w-0">
              <span className="font-semibold">{counts.pending} employee inactivity record{counts.pending === 1 ? ' has' : 's have'} not yet been reviewed.</span>{' '}
              Proceeding completes the daily review only — individual employee review statuses will not change.
            </p>
          </div>
        </div>

        <div className="min-w-0 px-5 py-3.5 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center gap-2" data-testid="day-review-footer">
          <button type="button" onClick={onReviewPending} disabled={busy} data-testid="day-review-pending-btn"
            className={`${btn} text-blue-700 bg-white border border-blue-200 hover:bg-blue-50 focus-visible:ring-blue-500 sm:mr-auto`}>
            <ListChecks className="h-4 w-4" aria-hidden="true" /> Review Pending Employees
          </button>
          <div className="flex items-center gap-2 ml-auto sm:ml-0">
            <button type="button" onClick={() => onOpenChange(false)} disabled={busy} data-testid="day-review-cancel-btn"
              className={`${btn} text-slate-600 hover:bg-slate-200/70 focus-visible:ring-slate-400`}>
              Cancel
            </button>
            <button type="button" onClick={onProceed} disabled={busy} data-testid="day-review-proceed-btn"
              className={`${btn} text-white bg-amber-600 hover:bg-amber-700 shadow-sm hover:shadow-md focus-visible:ring-amber-500`}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Proceed Anyway
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DayReviewConfirmDialog;
